import React, { FC, useMemo } from 'react'

import { ButtonProps } from '@Components/Button'
import { BuyButtonElement } from '@Components/BuyButton/BuyButtonElement'
import { BuyButtonWrapper } from '@Components/BuyButton/styledElements'
import { useAddToCart, UseAddToCartProps } from '@Globals/Cart/useAddToCart'

export type BuyButtonProps = {
  beforeAddToCart?: () => void
  buttonColor?: ButtonProps['color']
  buttonText?: string // default Add to Cart | Buy Now
  className?: HTMLButtonElement['className']
  disabled?: boolean
  handleOnClick?: () => void
  id?: string
  isSaleable: boolean
  showPrice: boolean
  unwrapped?: boolean
  updateProduct?: boolean
  variant?: ButtonProps['variant']
} & UseAddToCartProps

export const BuyButton: FC<BuyButtonProps> = ({
  additionalIDs,
  beforeAddToCart,
  buttonColor,
  buttonText,
  className,
  disabled = false,
  getPersonalizationData,
  handleOnClick,
  id,
  isSaleable,
  postProcess,
  product,
  quantity,
  showPrice,
  unwrapped,
  updateProduct = false,
  variant,
  variantID,
}) => {
  const { callback: onClick, inProgress } = useAddToCart({
    postProcess,
    product,
    quantity,
    additionalIDs,
    variantID,
    getPersonalizationData,
    updateProduct,
  })

  const formattedTotalBeforePrice = product.masterVariant?.price?.discount?.format
  const formattedTotalPrice = product.masterVariant?.price?.format

  // TODO: this might be replaced with a price component from the PDP - see TH-610 and TH-1045.
  const renderedPriceMarkup = useMemo(
    () => {
      if (!showPrice) {
        return null
      }

      if (formattedTotalBeforePrice) {
        return (
          <>
            <span className="beforePrice">{formattedTotalBeforePrice}</span>
            <span className="discountPrice">{formattedTotalPrice}</span>
          </>
        )
      }

      return (
        <span className="productPrice">{formattedTotalPrice}</span>
      )
    },
    [formattedTotalBeforePrice, formattedTotalPrice, showPrice],
  )

  const renderedButtonElement = (
    <BuyButtonElement
      buttonColor={buttonColor}
      buttonText={buttonText}
      className={className}
      disabled={disabled}
      id={id}
      inProgress={inProgress}
      isSaleable={isSaleable}
      onClick={async () => {
        if (beforeAddToCart) {
          beforeAddToCart()
        }

        if (handleOnClick) {
          handleOnClick()
          return null
        }
        if (onClick && !inProgress) {
          return onClick()
        }
        return null
      }}
      variant={variant}
    />
  )

  if (unwrapped) {
    return renderedButtonElement
  }

  return (
    <BuyButtonWrapper>
      <div className="buttonInner">
        {renderedPriceMarkup}
        {renderedButtonElement}
      </div>
    </BuyButtonWrapper>
  )
}
